'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const stats = [
{ value: '48k+', label: 'Pets Registered' },
{ value: '2,400', label: 'Businesses Listed' },
{ value: '96%', label: 'Reunited Rate' }];


export default function LandingHero() {
  return (
    <section className="pt-24 pb-16 lg:pt-28 lg:pb-20 bg-background overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-14 items-center">
          {/* Left column */}
          <div className="flex flex-col gap-6">
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 bg-secondary-tint text-secondary px-4 py-2 rounded-full w-fit">
              <Icon name="SparklesIcon" size={14} variant="solid" className="text-secondary" />
              <span className="eyebrow text-secondary">Philippines #1 Pet Platform</span>
            </div>

            {/* H1 */}
            <h1 className="text-hero text-foreground">
              The Complete Digital Home for Your{' '}
              <span className="text-primary">Furbabies</span>
            </h1>

            {/* Subheading */}
            <p className="text-base lg:text-lg text-body-text leading-relaxed max-w-lg font-medium">
              NFC Smart Tags, health records, community, and marketplace — everything your pet needs, all in one place. Give your pet a passport to the world! 🐾
            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <Link href="/sign-up-login-screen" className="btn-primary no-underline">
                <Icon name="RocketLaunchIcon" size={16} />
                Join Free
              </Link>
              <Link href="/marketplace" className="btn-outline no-underline">
                Browse Pets
              </Link>
              <Link href="#nfc" className="text-sm font-semibold text-secondary-text hover:text-primary transition-colors no-underline flex items-center gap-1">
                Learn More
                <Icon name="ArrowRightIcon" size={14} />
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-6 pt-2 border-t border-border">
              {stats?.map((stat) =>
              <div key={`hero-stat-${stat?.label}`} className="flex flex-col">
                  <span className="text-2xl font-extrabold text-foreground font-tabular">{stat?.value}</span>
                  <span className="text-xs text-muted-foreground font-semibold">{stat?.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="relative flex justify-center lg:justify-end">
            {/* Hero image */}
            <div className="relative w-full max-w-[480px]">
              <div className="rounded-2xl overflow-hidden aspect-[4/5] bg-gradient-to-br from-primary-tint via-secondary-tint to-accent-tint shadow-float">
                <AppImage
                  src="https://images.unsplash.com/photo-1621267707191-ba2261a2b804"
                  alt="Happy golden retriever dog sitting outdoors in sunlight, warm editorial photography"
                  fill
                  className="object-cover"
                  priority />
                
              </div>

              {/* Floating glass card 1 — NFC scan */}
              <div className="float-anim absolute -left-8 top-16 glass-card rounded-xl p-3 min-w-[180px] border border-white/60">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-tint flex items-center justify-center flex-shrink-0">
                    <Icon name="WifiIcon" size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">NFC Tag Scanned</p>
                    <p className="text-xs text-muted-foreground">Luna · BGC, Taguig</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-secondary animate-pulse"></div>
                  <span className="text-xs text-secondary font-semibold">Owner notified instantly</span>
                </div>
              </div>

              {/* Floating glass card 2 — vet rating */}
              <div className="float-anim-delay absolute -right-6 bottom-20 glass-card rounded-xl p-3 min-w-[170px] border border-white/60">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-secondary overflow-hidden">
                    <AppImage
                      src="https://img.rocket.new/generatedImages/rocket_gen_img_113950b5a-1772171833259.png"
                      alt="Filipino veterinarian in white coat smiling"
                      width={32}
                      height={32}
                      className="object-cover w-full h-full" />
                    
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">Dr. Santos, DVM</p>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5]?.map((s) =>
                      <Icon key={`star-${s}`} name="StarIcon" size={10} variant="solid" className="text-yellow" />
                      )}
                    </div>
                  </div>
                </div>
                <span className="badge-verified text-xs">✓ Verified Vet</span>
              </div>

              {/* Tag ID pill overlay */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-navy/90 text-white text-xs font-bold px-4 py-2 rounded-full backdrop-blur-sm flex items-center gap-2">
                <Icon name="IdentificationIcon" size={14} className="text-yellow" />
                PH-ID: 2024-00847
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}